/**
 * Text Case Formatting Utilities
 *
 * Per UI Standards (Points 3 & 4):
 * - Filter/dropdown names and values → Capital Case
 * - Chart/table headers → Capital Case
 *
 * Smart rules:
 * - Only capitalizes the first letter of each word (does NOT force-lowercase the rest)
 * - Acronyms (DBU, EBU, HR, CSpec) are preserved
 * - Parenthetical content preserved as-is
 * - Hyphens in compound words preserved (Bar-Line stays Bar-Line)
 * - snake_case keys converted: total_sales → Total Sales
 * - camelCase keys converted: totalSales → Total Sales
 */

export type TextCaseMode = 'capital' | 'camel' | 'none';

/**
 * Check if a word is a special token that should be completely untouched.
 */
function isSpecialToken(word: string): boolean {
  if (!word) return false;
  if (word.includes('/')) return true;        // DBU/EBU
  if (/^\d+$/.test(word)) return true;        // 2024
  if (/^[A-Z]\d+$/i.test(word)) return true;  // Q1, H2
  if (word === '$' || word === '%') return true;
  return false;
}

/**
 * Capitalize the first letter of a word WITHOUT lowercasing the rest.
 * This preserves acronyms (DBU stays DBU), mixed-case (CSpec stays CSpec),
 * while still fixing "stackbar" → "Stackbar".
 */
function smartCapitalize(word: string): string {
  if (!word) return '';
  if (isSpecialToken(word)) return word;
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * Convert a string to Capital Case.
 *
 * Behavior:
 * - "Mixed stackbar chart" → "Mixed Stackbar Chart"
 * - "DBU/EBU Direct" → "DBU/EBU Direct"
 * - "CSpec Bridge" → "CSpec Bridge"
 * - "Revenue ($) trend by Business Unit" → "Revenue ($) Trend By Business Unit"
 * - "Bar-Line" → "Bar-Line" (hyphen preserved)
 * - "total_sales" → "Total Sales" (underscore → space)
 * - "totalSales" → "Total Sales" (camelCase split)
 * - "Sales by Region" → "Sales By Region"
 * - "Q1 2024" → "Q1 2024"
 */
export function toCapitalCase(input: unknown): string {
  if (input === null || input === undefined) return '';
  const str = String(input).trim();
  if (!str) return '';

  // Step 1: Split camelCase boundaries (totalSales → total Sales)
  let result = str.replace(/([a-z])([A-Z])/g, '$1 $2');

  // Step 2: Replace underscores with spaces (total_sales → total sales)
  // Keep hyphens as-is (Bar-Line stays Bar-Line)
  result = result.replace(/_+/g, ' ');

  // Step 3: Capitalize first letter of each word (space-separated)
  // Hyphened words: capitalize each part (bar-line → Bar-Line)
  result = result
    .split(/\s+/)
    .filter(Boolean)
    .map(word => {
      if (isSpecialToken(word)) return word;
      // Handle hyphenated compounds: capitalize each part
      if (word.includes('-')) {
        return word.split('-').map(smartCapitalize).join('-');
      }
      return smartCapitalize(word);
    })
    .join(' ');

  // Step 4: Clean up spacing around parentheses
  // "Revenue( $ )" → normalize, but keep original spacing
  result = result.replace(/\s+/g, ' ').trim();

  return result;
}

/**
 * Convert a string to camelCase.
 * "total_sales" → "totalSales"
 * "Total Sales" → "totalSales"
 */
export function toCamelCase(input: unknown): string {
  if (input === null || input === undefined) return '';
  const str = String(input);
  if (!str.trim()) return '';
  const words = str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_\-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return '';
  return words
    .map((word, i) => {
      const lower = word.toLowerCase();
      return i === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

/**
 * Format any display text according to the requested case mode.
 */
export function formatDisplayText(input: unknown, mode: TextCaseMode = 'capital'): string {
  if (input === null || input === undefined) return '';
  switch (mode) {
    case 'capital':
      return toCapitalCase(input);
    case 'camel':
      return toCamelCase(input);
    case 'none':
    default:
      return String(input);
  }
}

/**
 * Keep existing numeric/special values as-is, only format string values.
 */
export function formatValueSafely(value: unknown, mode: TextCaseMode = 'capital'): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value === 'All' || value === 'all') return 'All';
  return formatDisplayText(value, mode);
}
